import { Component, Inject, Input, ViewContainerRef } from "@angular/core";
import { APP_CONFIG, IAppConfiguration } from "@bca-shared/models/config";
import * as composeActions from "@bca-compose/store/compose.actions";
import { DialogService } from "@bca-core/modules/dialog/services/dialog.service";
import { PageScrollService } from "@bca-core/modules/page-scroll/services/page-scroll.service";
import { EventBusClickService } from "@bca-core/services/event-bus.service";
import { RouterService } from "@bca-core/services/router.service";
import { UIService } from "@bca-core/services/ui.service";
import { ReceiptDialog } from "@bca-receipt/receipt/receipt-dialog";
import * as receiptActions from "@bca-receipt/store/receipt.actions";
import {
  DialogIdentifier,
  Route,
  ScrollPositionId,
} from "@bca-shared/models/_enums";
import { ICompany } from "@bca-shared/models/company";
import { DialogId } from "@bca-shared/models/_types";
import { pageData } from "@bca-shared/shared.constants";
import * as appActions from "@bca-store/app.actions";
import { IAction } from "@bca-store/models/action";
import { createAction } from "@bca-store/models/action-creator";
import { IRootState } from "@bca-store/store.reducers";
import {
  selectAllProducts,
  selectIncompletedProducts,
  selectProductsWithUsers,
  selectTotalPrice,
  selectTotalProductCount,
} from "@bca-store/store.selectors";
import * as userDataActions from "@bca-user-data/store/user-data.actions";
import { environment } from "@environments/environment";
import { select, Store } from "@ngrx/store";
import { Observable } from "rxjs";
import { combineLatest, filter, map, take } from "rxjs/operators";
import { TabComponent } from "./tab-stepper/tab/tab.component";

@Component({
  selector: "bca-flow-window",
  templateUrl: "bca-flow-window.component.html",
  styleUrls: ["bca-flow-window.component.scss"],
})
export class BcaFlowWindowComponent {
  @Input()
  showMobileReceiptFooter$ = this._uiService.showMobileReceiptFooter$;
  currentRoute$ = this._store$.pipe(select(({ app }) => app.currentRoute));
  totalProductCount$ = this._store$.pipe(select(selectTotalProductCount));
  totalReceiptPrice$ = this._store$.pipe(select(selectTotalPrice));
  isReceiptDialogOpen$ = this._store$.pipe(
    select(({ app }) => app.dialogStatus[DialogIdentifier.Receipt]),
  );
  stickyReceipt$ = this.totalProductCount$.pipe(map((count) => count < 4));
  showPhoneNumber$ = this._store$.pipe(select(({ app }) => app.showPhone));
  highestRoute$ = this._store$.select(({ app }) => app.highestRoute);
  productsLoading$ = this._store$.pipe(
    select(({ compose }) => compose.productsLoading),
  );
  selectedProductCompose$ = this._store$.pipe(
    select(({ compose }) => compose.selectedProduct),
  );
  incompletedProducts$ = this._store$.pipe(select(selectIncompletedProducts));
  redirectToIssuerLoading$ = this._store$.pipe(
    select(({ confirmation }) => confirmation.redirectToIssuerLoading),
  );

  showProductInfo$ = this._store$.pipe(
    select(selectTotalProductCount),
    map((count) => count === 1),
  );

  cartProducts$ = this._store$.pipe(select(selectProductsWithUsers));
  confirmedProductCount$ = this.cartProducts$.pipe(map((products) => products.length));
  cartSubTotalReceiptPrice$ = this.cartProducts$.pipe(
    map(products =>
      products.reduce((total, product) => {
        const selectedAddonsPrice = (product.addons || [])
          .filter(a => a.selected)
          .reduce((sum, a) => sum + (a.price || 0), 0);
        return total + product.price + selectedAddonsPrice;
      }, 0)
    ),
  );
  cartTax$: Observable<number>;
  cartTotalReceiptPrice$: Observable<number>;
  company$: Observable<ICompany>;

  keuzehulpUrlNL = "https://www.ns.nl/zakelijk/keuzehulp/vragenlijst";
  keuzehulpUrlEN = "https://www.ns.nl/en/business/decision-aid/vragenlijst";

  Route = Route;
  maxBuyableProducts = environment.receipt.maxBuyableProducts;
  currentRoute: Route;
  isThereAIncompleteProduct: boolean;

  pageData = pageData;

  constructor(
    private _routerService: RouterService,
    private _store$: Store<IRootState>,
    private _uiService: UIService,
    private _dialogService: DialogService,
    private _eventBus: EventBusClickService,
    private _page: PageScrollService,
    private _viewContainerRef: ViewContainerRef,
    @Inject(APP_CONFIG) private _cartConfig: IAppConfiguration,
  ) {
    this.cartTax$ = this.cartSubTotalReceiptPrice$.pipe(
      map((subTotal) => Math.round(subTotal * this._cartConfig.taxRate)),
    );
    this.cartTotalReceiptPrice$ = this.cartSubTotalReceiptPrice$.pipe(
      combineLatest(this.cartTax$),
      map(([subTotal, tax]) => subTotal + tax),
    );
    this.company$ = this._store$.pipe(
      select(({ receipt }) => receipt.company),
      filter((company) => company != null),
    );
  }

  nextRoute(): void {
    this.currentRoute$.subscribe((route) => (this.currentRoute = route));

    if (this.currentRoute === Route.COMPOSE) {
      this.selectedProductCompose$
        .pipe(take(1))
        .subscribe((selectedProduct) => {
          if (selectedProduct) {
            // Check if adding this product would exceed the limit
            this.cartProducts$.pipe(take(1)).subscribe((confirmedProducts) => {
              if (confirmedProducts.length >= this.maxBuyableProducts) {
                // Don't add if at max limit
                return;
              }
              
              // Safe to proceed - add product
              this._store$.dispatch(
                this._createAction(
                  new userDataActions.SelectProduct(selectedProduct),
                ),
              );
              this._store$.dispatch(
                this._createAction(new composeActions.SetConfiguredProduct(null)),
              );
              this._store$.dispatch(
                this._createAction(
                  new receiptActions.AddProduct(selectedProduct),
                ),
              );
              this._store$.dispatch(
                this._createAction(new composeActions.SelectProduct(null)),
              );
              this._store$.dispatch(this._createAction(new appActions.NextRoute()));
            });
          } else {
            // No product selected, just go to next route
            this._store$.dispatch(this._createAction(new appActions.NextRoute()));
          }
        });
    } else if (this.currentRoute === Route.VERIFY) {
      this._store$.dispatch(
        this._createAction(
          new receiptActions.SetVerifiedStatus({ verified: true }),
        ),
      );
      this._store$.dispatch(this._createAction(new appActions.NextRoute()));
    } else {
      this._uiService.nextPageClicked$.next();
    }
  }

  addNewProduct(): void {
    this.currentRoute$.subscribe((route) => (this.currentRoute = route));

    if (this.currentRoute === Route.COMPOSE) {
      this.selectedProductCompose$
        .pipe(
          take(1),
          filter((selectedProduct) => selectedProduct != null),
        )
        .subscribe((selectedProduct) => {
          // Check if adding this product would exceed the limit
          this.cartProducts$.pipe(take(1)).subscribe((confirmedProducts) => {
            if (confirmedProducts.length >= this.maxBuyableProducts) {
              // Don't add if at max limit
              return;
            }
            
            // Safe to proceed - add product
            this._store$.dispatch(
              this._createAction(new composeActions.SetConfiguredProduct(null)),
            );
            this._store$.dispatch(
              this._createAction(new receiptActions.AddProduct(selectedProduct)),
            );
            this._store$.dispatch(
              this._createAction(new composeActions.SelectProduct(null)),
            );
            this._page.scrollTo(ScrollPositionId.TOP, {
              timeout: 275,
              scrollOffset: 250,
            });
          });
        });
    } else {
      this._uiService.addProductClicked$.next();
    }
  }

  onTabClicked(tab: TabComponent): void {
    this.currentRoute$.subscribe((route) => (this.currentRoute = route));
    this.incompletedProducts$.subscribe((product) => {
      if (product.length) {
        this.isThereAIncompleteProduct = true;
      } else {
        this.isThereAIncompleteProduct = false;
      }
    });

    if (
      this.currentRoute === Route.COMPOSE &&
      this.isThereAIncompleteProduct &&
      tab.route > 1
    ) {
      return;
    }

    // Prevent backward navigation to COMPOSE if at max product limit
    if (tab.route === Route.COMPOSE && this.currentRoute > tab.route) {
      this.cartProducts$.pipe(take(1)).subscribe((confirmedProducts) => {
        if (confirmedProducts.length >= this.maxBuyableProducts) {
          return; // Block navigation silently
        }
        // Only proceed if NOT at max limit
        this._completeTabNavigation(tab);
      });
      return;
    }

    // Normal tab navigation
    this._completeTabNavigation(tab);
  }

  private _completeTabNavigation(tab: TabComponent): void {
    this._uiService.tabClicked$.next(tab);
    const { done } = tab;
    if (done) {
      // Reset highestRoute when navigating backwards so "next" from that step
      // advances one step at a time instead of jumping to the old highestRoute.
      if (tab.route < this.currentRoute) {
        this._store$.dispatch(
          this._createAction(new appActions.SetHighestRoute({ route: tab.route })),
        );
        // When going back to COMPOSE, clear userData selection and remove any
        // incomplete receipt products (added in nextRoute but without user data).
        if (tab.route === Route.COMPOSE) {
          this._store$.dispatch(
            this._createAction(new userDataActions.SelectProduct(null)),
          );
          this._store$.pipe(
            select(({ receipt }) => receipt.products.filter(p => !p.user || !p.user.valid)),
            take(1),
          ).subscribe(orphans => {
            orphans.forEach(p =>
              this._store$.dispatch(this._createAction(new receiptActions.DeleteProduct(p)))
            );
          });
        }
      }
      this._routerService.navigateTo(tab.route);
    }
  }

  onReceiptOpened(): void {
    const heigth = window.scrollY;
    const receiptDialog = this._dialogService.open(
      ReceiptDialog,
      DialogIdentifier.Receipt,
      this._viewContainerRef,
      null,
      null,
      (visible: boolean, identifier: DialogId) => {
        this._store$.dispatch(
          this._createAction(
            new appActions.SetDialogStatus({ visible, identifier }),
          ),
        );
      },
    );

    this._eventBus.closeClicked().subscribe((clicked) => {
      if (clicked === true) {
        receiptDialog.afterClosed().subscribe(() => {
          setTimeout(() => {
            window.scrollTo({
              top: heigth,
              left: 0,
              behavior: "smooth",
            });
          }, 100);
        });
      }
    });
    receiptDialog.instance.nextPage$().subscribe(() => this.nextRoute());
    receiptDialog.instance.addProduct$().subscribe(() => this.addNewProduct());
  }

  directToKeuzehulp(): void {
    const lang = document.documentElement.lang || "nl"; 
    const url = lang === "en" ? this.keuzehulpUrlEN : this.keuzehulpUrlNL;
    window.open(url, "_blank");
  }

  onActionClicked(action: appActions.Action): void {
    this._store$.dispatch(this._createAction(action));
  }

  cartModalOpen = false;

  openCartModal(): void {
    this.cartModalOpen = true;
  }

  closeCartModal(): void {
    this.cartModalOpen = false;
  }

  /**
   * Navigate directly to Besteloverzicht (Route.VERIFY).
   * Used by both the mobile floating cart modal button (steps 1 & 2)
   * and the desktop cart sidebar button (steps 1 & 2).
   * Does NOT affect step 3+ behavior — those still use nextRoute().
   */
  navigateToOverview(): void {
    this._routerService.navigateTo(Route.VERIFY);
  }

  navigateToCompanyData(): void {
    this._routerService.navigateTo(Route.COMPANY_DATA);
  }

  goToBesteloverzicht(): void {
    this.closeCartModal();
    this._routerService.navigateTo(Route.VERIFY);
  }

  private _createAction(action: IAction): IAction {
    return createAction(action, BcaFlowWindowComponent);
  }
}
